import AddIcon from '@mui/icons-material/Add';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import type { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { isDraftInvoiceApprovable, isInvoiceApprovable, isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import type { InvoiceBillingStatus } from '@vy/types';
import {
  INVOICE_BILLING_STATUSES,
  INVOICE_REVIEW_STATUSES,
  type IntegrationProviders,
} from '@vy/types';
import { JOB_TASK_STATUSES } from '@vy/jobs-types';
import { extract, isEmptyArray, isEmptyString, isNotNullOrUndefined, isNullOrUndefined, isSingleItem } from '@vy/utils';
import React, { useMemo } from 'react';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useMenu } from '../../../../../hooks/use-menu';
import { ApproveDraftInvoiceDialog } from '../../../invoices-components/approve-draft-invoice-dialog/ApproveDraftInvoiceDialog';
import { AddInvoiceNotesForm } from '../../../DynamicInvoiceViewPage/dynamic-invoice-view-components/dynamic-invoice-header/dynamic-invoice-header-forms/AddInvoiceNotesForm';
import { AddInvoicePurchaseOrderForm } from '../../../DynamicInvoiceViewPage/dynamic-invoice-view-components/dynamic-invoice-header/dynamic-invoice-header-forms/AddInvoicePurchaseOrderForm';
import { AddToInvoiceMenu } from './AddToInvoiceMenu';
import { BillingStatusMenu } from './BillingStatusMenu';
import { DeleteInvoiceMenu } from './DeleteInvoiceMenu';
import { DownloadMenu } from './DownloadMenu';
import { getRecipients, SendReminder } from './SendReminder/SendReminder';
import { ShareInvoiceMenu } from './ShareInvoiceMenu';
import { SyncInvoiceMenu } from './SyncInvoiceMenu';

type InvoicesActionsMenuProps = {
  children?: React.ReactElement;
  invoices: InvoiceOverview[];
  onApproveInvoices?: () => void;
  onDownloadAsPdf?: () => void;
  onDownloadAsPdfStatement?: () => void;
  onDownloadUsageEventsReport?: () => void;
  hasUsageEventsReportConfiguration?: boolean;
  onDeleteInvoice?: () => void;
  onUpdateInvoicesBillingStatus?: (billingStatus: InvoiceBillingStatus) => void;
  onSyncToStripe?: () => void;
  onSyncToNetSuite?: () => void;
  onSyncToNetSuiteSalesOrder?: () => void;
  onSyncToQuickBooks?: () => void;
  onSyncToMorning?: () => void;
  onSyncToMarketplace?: () => void;
  onSyncToXero?: () => void;
  onSyncToPaddle?: () => void;
  onMarkAsPaid?: () => void;
  onRefund?: () => void;
  onGrantCredits?: () => void;
  onApplyCredit?: () => void;
  onZenModeClick?: () => void;
  onSuccessfulRemoval?: () => void;
  onCreateInvoice?: () => void;
  connectedIntegrations: IntegrationProviders[];
  syncToNetSuiteDisabled?: boolean;
  disablePaidMark?: boolean;
  disableRefund?: boolean;
  disableCreditGrant?: boolean;
  disableAddToInvoice?: boolean;
  contractName: string;
};

export const InvoicesActionsMenu: React.FC<InvoicesActionsMenuProps> = ({
  invoices,
  onApproveInvoices,
  onUpdateInvoicesBillingStatus,
  onDownloadAsPdf,
  onDownloadAsPdfStatement,
  onDownloadUsageEventsReport,
  hasUsageEventsReportConfiguration,
  onSyncToStripe,
  onSyncToNetSuite,
  onSyncToNetSuiteSalesOrder,
  onSyncToQuickBooks,
  onSyncToMorning,
  onSyncToMarketplace,
  onSyncToXero,
  onSyncToPaddle,
  onMarkAsPaid,
  onRefund,
  onGrantCredits,
  onApplyCredit,
  onZenModeClick,
  syncToNetSuiteDisabled,
  connectedIntegrations,
  disablePaidMark,
  disableRefund = true,
  disableCreditGrant = true,
  disableAddToInvoice,
  children,
  onSuccessfulRemoval,
  onCreateInvoice,
  contractName,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const {
    isDialogOpen: isDraftApprovalDialogOpen,
    handleOpen: handleOpenDraftApprovalDialog,
    handleClose: handleCloseDraftApprovalDialog,
  } = useDialog();
  const {
    isOpen: isPoDialogOpen,
    handleMenuOpen: handleOpenAddPoDialog,
    handleMenuClose: handleCloseAddPoDialog,
  } = useMenu();
  const {
    isOpen: isAddDescriptionOpen,
    handleMenuOpen: handleOpenAddDescriptionDialog,
    handleMenuClose: handleCloseAddDescriptionDialog,
  } = useMenu();

  const areAllCustomersSynced = useMemo(() => {
    const customers = extract(invoices, 'customer');

    return customers.every((customer) =>
      isNotNullOrUndefined(customer.settings?.paymentProviderMetadata?.providerExternalId),
    );
  }, [invoices]);

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      handleMenuClose();
      action?.();
    };
  };

  const isAllInvoicesSyncable = invoices.every(isInvoiceSyncToIntegrationAllowed);

  const hasNotApprovedInvoices = invoices.some(
    (invoice) => invoice.reviewStatus !== INVOICE_REVIEW_STATUSES.Approved,
  );

  const hasCancelledInvoices = invoices.some(
    (invoice) => invoice.billingStatus === INVOICE_BILLING_STATUSES.Rejected,
  );

  const hasPaidInvoices = invoices.some(
    (invoice) => invoice.billingStatus === INVOICE_BILLING_STATUSES.Paid,
  );

  const isRefundOrCreditDisabled = hasNotApprovedInvoices || hasCancelledInvoices;

  const hasNoSelectedInvoices = isEmptyArray(invoices);
  const isSingleInvoiceOperation = isSingleItem(invoices);
  const invoice = invoices.at(0);
  const invoiceIds = invoices.map((inv) => inv.id);
  const isAddToInvoiceDisabled = disableAddToInvoice ?? invoices.length === 0;
  const isMarketplaceSyncDisabled = invoices.some(
    (invoice) =>
      invoice.marketplaceSyncStatus?.status === JOB_TASK_STATUSES.Completed ||
      isEmptyString(invoice.customer.settings?.cloudProviderSettings?.cloudProvider),
  );

  // A DRAFT invoice is still accumulating usage; it may be approved (individually
  // or in bulk) but only behind a warning dialog. Other non-approvable invoices
  // (already approved, future, accumulation children) still block approval.
  const isApprovableIncludingDraft = (invoice: InvoiceOverview) =>
    isInvoiceApprovable(invoice) || isDraftInvoiceApprovable(invoice);

  const approveDisabled = hasNoSelectedInvoices || !invoices.every(isApprovableIncludingDraft);

  const hasDraftInvoiceToApprove = invoices.some(isDraftInvoiceApprovable);

  const handleApproveClick = () => {
    handleMenuClose();
    if (hasDraftInvoiceToApprove) {
      handleOpenDraftApprovalDialog();
      return;
    }
    onApproveInvoices?.();
  };

  return (
    <div
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
      }}
      className="flex justify-center"
    >
      {isNotNullOrUndefined(children) &&
        React.cloneElement(children, {
          onClick: handleMenuOpen,
        })}
      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              width: 220,
              paddingTop: '8px',
              alignItems: 'stretch',
            },
          },
        }}
      >
        <Stack>
          {isNotNullOrUndefined(onCreateInvoice) && (
            <>
              <MenuItem
                {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.createInvoice)}
                onClick={handleMenuItemClick(onCreateInvoice)}
              >
                <ListItemIcon>
                  <AddIcon style={{ color: 'black' }} />
                </ListItemIcon>
                <ListItemText>New invoice</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem
            {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.approve)}
            onClick={handleApproveClick}
            disabled={approveDisabled}
          >
            <ListItemIcon>
              <VyIcon icon="DoneCheck" color="black" strokeOpacity={1} />
            </ListItemIcon>
            <ListItemText>Approve invoice</ListItemText>
          </MenuItem>

          {isNotNullOrUndefined(onUpdateInvoicesBillingStatus) && (
            <MenuItem disabled={hasNoSelectedInvoices}>
              <ListItemIcon>
                <VyIcon icon="DocumentFileSettingHorizontalBottomLeft" color="black" />
              </ListItemIcon>
              <BillingStatusMenu
                onClose={handleMenuClose}
                onUpdateInvoicesBillingStatus={(status) => onUpdateInvoicesBillingStatus?.(status)}
                hasNotApprovedInvoices={hasNotApprovedInvoices}
                hasPaidInvoices={hasPaidInvoices}
              >
                <ListItemText>Change status</ListItemText>
              </BillingStatusMenu>
            </MenuItem>
          )}

          {!disablePaidMark && (
            <MenuItem
              {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.markPaid)}
              className="pl-4"
              onClick={handleMenuItemClick(onMarkAsPaid)}
              disabled={hasNoSelectedInvoices || isUnpaidInvoice(invoices) || !isInvoiceApproved(invoices)}
            >
              <ListItemIcon>
                <VyIcon icon="CurrencyDollarSendFastSquare" color="black" strokeOpacity={1} />
              </ListItemIcon>
              <ListItemText>Receive payment</ListItemText>
            </MenuItem>
          )}

          {isNotNullOrUndefined(onZenModeClick) && (
            <MenuItem onClick={handleMenuItemClick(onZenModeClick)} disabled={hasNoSelectedInvoices}>
              <ListItemIcon>
                <CenterFocusWeakIcon
                  style={{
                    color: 'black',
                  }}
                />
              </ListItemIcon>
              <ListItemText>Zen mode</ListItemText>
            </MenuItem>
          )}

          {isSingleInvoiceOperation && canSendReminder(invoices.at(0)) && (
            <SendReminder invoices={invoices} />
          )}

          {!disableRefund && (
            <MenuItem onClick={handleMenuItemClick(onRefund)} disabled={isRefundOrCreditDisabled}>
              <ListItemIcon>
                <VyIcon icon="RefundRecipe" color="black" strokeOpacity={1} />
              </ListItemIcon>
              <ListItemText>Refund invoice</ListItemText>
            </MenuItem>
          )}

          {!disableCreditGrant && isSingleInvoiceOperation && (
            <MenuItem onClick={handleMenuItemClick(onGrantCredits)} disabled={isRefundOrCreditDisabled}>
              <ListItemIcon>
                <VyIcon icon="WalletOpenCheckmark" color="black" strokeOpacity={1} />
              </ListItemIcon>
              <ListItemText>Grant Credits</ListItemText>
            </MenuItem>
          )}

          {!disableCreditGrant && isSingleInvoiceOperation && (
            <MenuItem
              onClick={handleMenuItemClick(onApplyCredit)}
            >
              <ListItemIcon>
                <VyIcon icon="WalletOpenCheckmark" color="black" strokeOpacity={1} />
              </ListItemIcon>
              <ListItemText>Apply credit</ListItemText>
            </MenuItem>
          )}

          <Divider />

          <DownloadMenu
            handleMenuClose={handleMenuClose}
            onDownloadAsPdf={onDownloadAsPdf}
            onDownloadAsPdfStatement={onDownloadAsPdfStatement}
            onDownloadUsageEventsReport={onDownloadUsageEventsReport}
            hasUsageEventsReportConfiguration={hasUsageEventsReportConfiguration}
            disabled={hasNoSelectedInvoices}
          />

          <Divider />

          <ShareInvoiceMenu invoices={invoices} disabled={hasNoSelectedInvoices} />
          {!isAddToInvoiceDisabled && (
            <AddToInvoiceMenu
              handleOpenAddPoDialog={handleOpenAddPoDialog}
              handleOpenAddDescriptionDialog={handleOpenAddDescriptionDialog}
            />
          )}

          <Divider />

          <SyncInvoiceMenu
            disabled={hasNoSelectedInvoices}
            onSyncToStripe={onSyncToStripe}
            onSyncToNetSuite={onSyncToNetSuite}
            onSyncToNetSuiteSalesOrder={onSyncToNetSuiteSalesOrder}
            onSyncToQuickBooks={onSyncToQuickBooks}
            onSyncToMorning={onSyncToMorning}
            onSyncToMarketplace={onSyncToMarketplace}
            onSyncToXero={onSyncToXero}
            onSyncToPaddle={onSyncToPaddle}
            connectedIntegrations={connectedIntegrations}
            syncToNetSuiteDisabled={syncToNetSuiteDisabled}
            syncToStripeDisabled={!areAllCustomersSynced}
            handleMenuClose={handleMenuClose}
            isInvoiceSyncable={isAllInvoicesSyncable}
            isMarketplaceSynced={isMarketplaceSyncDisabled}
          />

          <Divider />
          {isSingleInvoiceOperation && (
            <DeleteInvoiceMenu
              disabled={isEmptyArray(invoices)}
              invoiceId={extractInvoiceId(invoices)}
              invoiceName={extractInvoiceName(invoices)}
              contractName={contractName}
              onSuccessfulRemoval={onSuccessfulRemoval}
            />
          )}
        </Stack>
      </Menu>
      {hasDraftInvoiceToApprove && (
        <ApproveDraftInvoiceDialog
          open={isDraftApprovalDialogOpen}
          onClose={handleCloseDraftApprovalDialog}
          onConfirm={() => onApproveInvoices?.()}
        />
      )}
      {!isAddToInvoiceDisabled && (
        <>
          <AddInvoicePurchaseOrderForm
            invoiceIds={invoiceIds}
            open={isPoDialogOpen}
            onClose={handleCloseAddPoDialog}
            existingPurchaseOrder={invoice?.purchaseOrder}
          />

          <AddInvoiceNotesForm
            invoiceIds={invoiceIds}
            open={isAddDescriptionOpen}
            onClose={handleCloseAddDescriptionDialog}
            existingNotes={invoice?.notes}
          />
        </>
      )}
    </div>
  );
};

function isInvoiceApproved(invoices: InvoiceOverview[]): boolean {
  return invoices.at(0)?.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
}

function isUnpaidInvoice(invoices: InvoiceOverview[]): boolean {
  return invoices.at(0)?.billingStatus === INVOICE_BILLING_STATUSES.Paid;
}

function extractInvoiceId(invoices: InvoiceOverview[]): string | undefined {
  return invoices.at(0)?.id;
}

function extractInvoiceName(invoices: InvoiceOverview[]): string {
  return invoices.at(0)?.name ?? '';
}

function canSendReminder(invoice?: InvoiceOverview): boolean {
  if (isNullOrUndefined(invoice)) {
    return false;
  }

  const emails = getRecipients(invoice.customer.contacts);

  if (isEmptyArray(emails)) {
    return false;
  }

  return invoice.wasInvoiceEmailSent && invoice.billingStatus !== INVOICE_BILLING_STATUSES.Paid;
}
